import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../../Components/Common/Layout/Layout';
import Modal from '../../Components/Common/Modal/Modal';
import Pagination from '../../Components/Common/Pagination/Pagination';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import Loading from '../../Components/Loading/Loading';
import { errorMsg } from '../../constants/common.constant';
import useCompanyEvent from '../../hooks/data/useCompanyEvent';
import useContainerWidth from '../../hooks/useContainerWidth';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import { CompanyEvent } from '../../types/companyevent.type';
import { getMaxPageNum, paginate } from '../../utils/pagination';
import { EventType, WidgetNameSource } from '../../utils/type';
import './CompanyEventWidget.css';
import EventDetail from './components/EventDetail';
import EventListItem from './components/EventListItem';

export interface CompanyEventWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  eventType?: EventType;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const ITEMS_PER_PAGE = 10;

const CompanyEventWidget: React.FC<CompanyEventWidgetProps> = ({
  theme,
  token = '',
  symbol = '',
  onHeightChange,
  eventType = EventType.All,
  onError,
  onNoData,
  onLoadingChange,
}) => {
  const [selectedEvent, setSelectedEvent] = useState<CompanyEvent | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const { isSmallScreen, width } = useContainerWidth(containerRef);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.CompanyEvent,
    onHeightChange,
  );

  // Detect param changes during render to reset page immediately (before API call)
  const lastParamsRef = useRef({ symbol, eventType });
  const paramsChanged =
    lastParamsRef.current.symbol !== symbol ||
    lastParamsRef.current.eventType !== eventType;
  if (paramsChanged) {
    lastParamsRef.current = { symbol, eventType };
  }
  const apiPage = paramsChanged ? 1 : currentPage;

  const {
    data: events,
    totalItems,
    isLoading,
  } = useCompanyEvent(
    symbol,
    eventType,
    token,
    apiPage,
    ITEMS_PER_PAGE,
    onError,
    onNoData,
  );

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync state after param change
  useEffect(() => {
    setSelectedEvent(null);
    setCurrentPage(1);
  }, [symbol, eventType]);

  // Mobile: clear selection. Desktop: auto-select first when data ready and nothing selected.
  useEffect(() => {
    if (width === 0) return;
    if (isSmallScreen) {
      setSelectedEvent(null);
      setModalOpen(false);
      return;
    }
    if (!isLoading && selectedEvent === null && events.length > 0) {
      setSelectedEvent(events[0]);
    }
  }, [isSmallScreen, width, isLoading, events]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxPages = getMaxPageNum(width, !isSmallScreen, false, false);
  const pagination = paginate(
    currentPage,
    ITEMS_PER_PAGE,
    totalItems,
    maxPages,
  );

  const handleEventClick = useCallback((event: CompanyEvent) => {
    setSelectedEvent(event);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handlePageClick = useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedEvent(null);
  }, []);

  return (
    <Layout theme={theme!}>
      <div className="company-event-container" ref={containerRef}>
        {isLoading && !onLoadingChange ? (
          <div className="company-event-skeleton-loading-container">
            <Loading />
          </div>
        ) : !isLoading && events.length === 0 ? (
          <ErrorMessage msg={errorMsg.find((m) => m.key === 'companyEvents')} />
        ) : null}
        {!isLoading && events.length > 0 && (
          <div className="company-event-body">
            <div className="company-event-list-panel">
              <div className="event-list">
                {events.map((event) => (
                  <EventListItem
                    key={event.eventId}
                    event={event}
                    isSelected={selectedEvent?.eventId === event.eventId}
                    onClick={handleEventClick}
                  />
                ))}
              </div>

              <div style={{ marginRight: '8px' }}>
                <Pagination
                  pagination={pagination}
                  onPageClick={handlePageClick}
                  haveLastPage={true}
                />
              </div>
            </div>

            <div className="company-event-detail-panel">
              {selectedEvent && <EventDetail event={selectedEvent} />}
            </div>
          </div>
        )}

        {isSmallScreen && (
          <Modal isOpen={modalOpen} onClose={handleModalClose} withCloseButton>
            {selectedEvent && <EventDetail event={selectedEvent} />}
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default CompanyEventWidget;
